import { useMemo } from "react";
import { Card, Flex, Text } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { ComplianceObjectType } from "~/graphql/supergraphEnums";
import { getInvestingProfileName } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/utils";
import { formatShortFormDate } from "~/utils/datetime";
import { AddressViewOnly } from "../../../CaseDetailsDrawer/common/AddressViewOnly";
import { ProfileNameFields } from "../../../utils/types";
import { DetailsRow } from "../DetailsRow";
import { profileDetailsHeader } from "../styles_vanilla.css";
import { ProfileAddressFields } from "./types";

type Props = ProfileNameFields & {
  dateOfBirth?: string | null;
  dateOfOrganization?: string | null;
  addresses?: ProfileAddressFields[] | null;
};

export const ProfileDetails = ({ profileInfo }: { profileInfo: Props | null }) => {
  const profileDetails = useMemo(() => {
    if (!profileInfo) return null;

    const isIndividual = profileInfo.complianceObjectType === ComplianceObjectType.Individual;
    const name = getInvestingProfileName(profileInfo) || "--";
    const rawDate = isIndividual ? profileInfo.dateOfBirth : profileInfo.dateOfOrganization;
    const dateLabel = isIndividual ? "Date of birth" : "Date of formation";

    const items: { label: string; value: string }[] = [
      { label: "Related profile", value: name },
      { label: "Legal name", value: name },
    ];

    if (rawDate) {
      items.push({ label: dateLabel, value: formatShortFormDate(rawDate) });
    }

    return items;
  }, [profileInfo]);

  const addressDetails = useMemo(() => {
    if (!profileInfo || !profileInfo.addresses) return null;

    const primaryAddress = profileInfo.addresses[0];

    return {
      hasAddress: !!primaryAddress,
      address: primaryAddress
        ? {
            street: primaryAddress.street ?? "",
            street1: primaryAddress.street2 ?? "",
            city: primaryAddress.city ?? "",
            stateCode: primaryAddress.state?.code ?? "",
            province: primaryAddress.province ?? "",
            zipcode: primaryAddress.postalCode ?? "",
            country: primaryAddress.country?.name ?? undefined,
          }
        : null,
    };
  }, [profileInfo]);

  return (
    <Card>
      <Text className={profileDetailsHeader}>Profile Details</Text>
      <Flex flexDirection="column" gap={Sizes.MD}>
        {profileDetails?.map((item) => (
          <DetailsRow key={item.label} label={item.label} value={item.value} />
        ))}
        {addressDetails?.hasAddress && (
          <AddressViewOnly
            hasAddress={addressDetails.hasAddress}
            street={addressDetails.address?.street}
            street1={addressDetails.address?.street1}
            city={addressDetails.address?.city}
            stateCode={addressDetails.address?.stateCode}
            province={addressDetails.address?.province}
            zipcode={addressDetails.address?.zipcode}
            country={addressDetails.address?.country}
            showAsTable
          />
        )}
      </Flex>
    </Card>
  );
};
